/**
 * The window being edited, against the containers (user, 18/10/2026: after
 * updating the window, the containers with drops outside it must show):
 * which ones would have drop times outside it — not sent — before Save, and
 * which of those are new with this change. The planner's own timing rules.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { CheckCircle2, Moon } from 'lucide-react';

import type { DropGenContainerSummary } from '../../../../drop-generator.types';
import { planDay } from '../../../../utils/clock/plan-day';
import { containersCut, type OutsideContainer } from '../../../../utils/drops/outside-window';
import { computeContainerTimes } from '../../../../utils/slot-times';

interface Props {
  containers: DropGenContainerSummary[];
  tz: string | null;
  draft: { startMin: number; endMin: number };
  saved: { startMin: number; endMin: number };
}

function cutWith(containers: DropGenContainerSummary[], tz: string | null, w: { startMin: number; endMin: number }): OutsideContainer[] {
  const day = tz ? planDay({ recipientTimezone: tz, windowStartMin: w.startMin }) : undefined;
  return containersCut(containers, computeContainerTimes(containers, { ...w, ...(day ? { day } : {}) }));
}

const share = (c: OutsideContainer) => (c.cut === c.total ? `all ${c.total}` : `${c.cut} of ${c.total}`);

export const WindowImpact: FC<Props> = ({ containers, tz, draft, saved }) => {
  const now = cutWith(containers, tz, draft);
  const before = new Set(cutWith(containers, tz, saved).map((c) => c.id));
  if (containers.every((c) => !c.enabled)) return null;
  if (now.length === 0)
    return (
      <p className="flex items-center gap-1.5 text-xs text-success" data-testid="window-impact">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Every container&apos;s drops are inside this window.
      </p>
    );
  return (
    <div className="flex flex-col gap-1 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs" data-testid="window-impact">
      <span className="flex items-center gap-1.5 font-medium text-warning">
        <Moon className="h-3.5 w-3.5" aria-hidden />
        With this window, {now.length} container{now.length === 1 ? ' has' : 's have'} drop times outside it — not sent
      </span>
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-muted-foreground">
        {now.map((c) => (
          <li key={c.id}>
            <b className="font-medium text-foreground">{c.name}</b> {share(c)}
            {before.has(c.id) ? '' : <span className="ml-1 font-semibold text-warning">new</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};
